// Shared fetch + normalization for the Dashboard's task widgets (Meine
// Aufgaben, Diese Woche fällig, Überfällig, Heute fällig). Combines project
// tasks assigned to me with my personal (list) tasks into one unified shape
// so the widgets can filter/sort without caring about the source.

import { api } from "./api";
import type { PersonalTask, Task } from "@juuplan/shared";

/** A project task annotated by GET /api/me/tasks. */
export interface MeTask extends Task {
  project_id: number;
  project_name: string;
  project_color: string | null;
}

export interface DashboardTaskItem {
  uid: string;
  title: string;
  /** ISO date (YYYY-MM-DD), or null when the task has no due date. */
  due: string | null;
  isMilestone: boolean;
  /** Where to navigate when the item is clicked. */
  href: string;
  source: "project" | "personal";
  /** Label shown next to the title (project name, or "Persönlich"). */
  sourceLabel: string;
}

function projectTaskToItem(t: MeTask): DashboardTaskItem {
  return {
    uid: `project-${t.id}`,
    title: t.title,
    due: t.end_date ?? t.deadline ?? null,
    isMilestone: t.is_milestone,
    href: `/p/${t.project_id}?task=${t.id}`,
    source: "project",
    sourceLabel: t.project_name,
  };
}

function personalTaskToItem(t: PersonalTask): DashboardTaskItem {
  return {
    uid: `personal-${t.id}`,
    title: t.title,
    due: t.due_date,
    isMilestone: false,
    href: `/lists/${t.list_id ?? "none"}?task=${t.id}`,
    source: "personal",
    sourceLabel: "Persönlich",
  };
}

/** Every open (not-done) task assigned to me: project tasks + personal tasks. */
export async function fetchMyOpenTasks(): Promise<DashboardTaskItem[]> {
  const [projectTasks, personalTasks] = await Promise.all([
    api<MeTask[]>("/api/me/tasks"),
    api<PersonalTask[]>("/api/personal-tasks"), // include_done defaults to false
  ]);
  return [
    ...projectTasks.filter((t) => t.status !== "done").map(projectTaskToItem),
    ...personalTasks.map(personalTaskToItem),
  ];
}
